/**
 * NeuralOrb — HiBFF's global AI thinking indicator (founder-supplied design).
 * Animated Neural Energy Orb: dark translucent core, glowing violet rings,
 * flowing orange/magenta energy ribbons, continuous rotation + breathing glow.
 * Sizes: sm (inline replies), md (chat), lg (full-screen). `bare` renders the
 * orb without the black card; `label` shows "{AvA name} is thinking".
 */
import React from 'react';
import './NeuralOrb.css';

const SIZES = { sm: 40, md: 72, lg: 145 };

export const NeuralOrb = ({ size = 'md', label = null, bare = true, className = '' }) => {
  const px = SIZES[size] || SIZES.md;
  // Unique SVG def ids per instance — duplicate ids across mounted orbs made
  // url(#...) resolve into a hidden twin and render a blank orb on mobile.
  const uid = React.useId().replace(/[:]/g, '');
  const svg = (
    <svg
      viewBox="0 0 160 160"
      className="neural-orb"
      style={{ width: px, height: px }}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`core-${uid}`}>
          <stop offset="0%" stopColor="#100821" />
          <stop offset="75%" stopColor="#080312" />
          <stop offset="100%" stopColor="#210A35" />
        </radialGradient>
        <linearGradient id={`violet-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#45A8FF" />
          <stop offset="50%" stopColor="#A13BFF" />
          <stop offset="100%" stopColor="#F05AFF" />
        </linearGradient>
        <linearGradient id={`energy-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop stopColor="#FF4BCB" />
          <stop offset="45%" stopColor="#FF8B35" />
          <stop offset="75%" stopColor="#FF4A9D" />
          <stop offset="100%" stopColor="#8B42FF" />
        </linearGradient>
        <filter id={`glow-${uid}`}>
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* Spherical core */}
      <circle cx="80" cy="80" r="48" fill={`url(#core-${uid})`} stroke="#9B43FF" strokeWidth="1.5" />
      {/* Rotating outer energy rings */}
      <g className="orb-rotate">
        <ellipse cx="80" cy="80" rx="48" ry="24" fill="none" stroke={`url(#violet-${uid})`} strokeWidth="2" filter={`url(#glow-${uid})`} />
      </g>
      <g className="orb-reverse">
        <ellipse cx="80" cy="80" rx="47" ry="32" fill="none" stroke={`url(#violet-${uid})`} strokeWidth="1.5" opacity=".8" />
      </g>
      {/* Flowing energy ribbons */}
      <g filter={`url(#glow-${uid})`}>
        <path
          className="orb-wave"
          d="M34 83 C53 38 91 42 126 78
             C145 102 108 119 80 98
             C57 82 44 68 34 83"
          fill="none" stroke={`url(#violet-${uid})`} strokeWidth="2"
        />
        <path
          className="orb-wave orb-wave-delay"
          d="M33 85 C55 110 79 106 103 88
             C124 72 137 86 126 94
             C104 117 67 119 33 85"
          fill="none" stroke={`url(#energy-${uid})`} strokeWidth="2.5"
        />
        <path
          className="orb-wave"
          d="M37 77 C63 104 91 104 125 83"
          fill="none" stroke={`url(#energy-${uid})`} strokeWidth="1.5" opacity=".9"
        />
      </g>
      {/* Outer luminous edge */}
      <circle cx="80" cy="80" r="48" fill="none" stroke={`url(#violet-${uid})`} strokeWidth="1.5" filter={`url(#glow-${uid})`} />
    </svg>
  );

  return (
    <div
      className={`${bare ? 'neural-orb-inline' : 'neural-orb-card'} ${className}`}
      role="status"
      aria-label={label || 'AI is thinking'}
      data-testid="neural-orb"
    >
      {svg}
      {label && <span className="neural-orb-label" data-testid="neural-orb-label">{label}</span>}
    </div>
  );
};

export default NeuralOrb;
